import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { Activity, Search, Filter, Download, Clock, User, FileText, Eye, Edit, Trash2, Plus, Lock, Unlock, Shield, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_ICONS = {
  view: Eye, edit: Edit, create: Plus, delete: Trash2,
  contact: FileText, lock: Lock, unlock: Unlock,
  access_attempt: Shield, '2fa_enabled': Shield, '2fa_disabled': Shield,
  finance_access_granted: Shield, reassign: RefreshCw,
};

const ACTION_COLORS = {
  view: 'bg-blue-50 text-blue-700 border-blue-200',
  edit: 'bg-amber-50 text-amber-700 border-amber-200',
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  delete: 'bg-red-50 text-red-700 border-red-200',
  contact: 'bg-purple-50 text-purple-700 border-purple-200',
  lock: 'bg-orange-50 text-orange-700 border-orange-200',
  unlock: 'bg-teal-50 text-teal-700 border-teal-200',
  access_attempt: 'bg-rose-50 text-rose-700 border-rose-200',
};

const RECORD_TYPES = ['all', 'client', 'lead', 'deal', 'project', 'task', 'invoice', 'user', 'security'];
const ACTION_TYPES = ['all', 'view', 'edit', 'create', 'delete', 'contact', 'lock', 'unlock', 'access_attempt'];

export const AuditTrailPage = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('all');
  const [filterRecordType, setFilterRecordType] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, usersRes] = await Promise.all([
        axios.get(`${API}/activity-logs?limit=500`),
        axios.get(`${API}/users`).catch(() => ({ data: [] }))
      ]);
      setLogs(logsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filterUser !== 'all' && log.user_id !== filterUser) return false;
    if (filterRecordType !== 'all' && log.record_type !== filterRecordType) return false;
    if (filterAction !== 'all' && log.activity_type !== filterAction) return false;
    if (search) {
      const q = search.toLowerCase();
      return (log.user_name || '').toLowerCase().includes(q) ||
        (log.record_name || '').toLowerCase().includes(q) ||
        (log.details || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Stats
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayLogs = logs.filter(l => l.timestamp?.startsWith(todayStr));
  const uniqueUsers = new Set(logs.map(l => l.user_id)).size;
  const createActions = logs.filter(l => l.activity_type === 'create').length;
  const editActions = logs.filter(l => l.activity_type === 'edit').length;

  const handleExport = () => {
    const csv = [
      ['Timestamp', 'User', 'Email', 'Action', 'Record Type', 'Record', 'Details'].join(','),
      ...filteredLogs.map(l => [
        l.timestamp, l.user_name, l.user_email, l.activity_type,
        l.record_type, l.record_name || '', (l.details || '').replace(/,/g, ';')
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_trail_${todayStr}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit trail exported');
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading audit logs...</div></div>;

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Audit Trail</h1>
          <p className="text-muted-foreground mt-1">System-wide activity log and security audit</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} size="sm"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button onClick={handleExport} size="sm"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center"><Activity className="h-5 w-5 text-blue-600" /></div>
            <div><p className="text-sm text-muted-foreground">Today's Actions</p><p className="text-2xl font-bold font-mono">{todayLogs.length}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center"><User className="h-5 w-5 text-emerald-600" /></div>
            <div><p className="text-sm text-muted-foreground">Active Users</p><p className="text-2xl font-bold font-mono">{uniqueUsers}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center"><Plus className="h-5 w-5 text-amber-600" /></div>
            <div><p className="text-sm text-muted-foreground">Records Created</p><p className="text-2xl font-bold font-mono">{createActions}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center"><Edit className="h-5 w-5 text-purple-600" /></div>
            <div><p className="text-sm text-muted-foreground">Edits Made</p><p className="text-2xl font-bold font-mono">{editActions}</p></div>
          </div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search logs..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="audit-search" />
        </div>
        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Users" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterRecordType} onValueChange={setFilterRecordType}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Record Type" /></SelectTrigger>
          <SelectContent>
            {RECORD_TYPES.map(t => <SelectItem key={t} value={t}>{t === 'all' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map(t => <SelectItem key={t} value={t}>{t === 'all' ? 'All Actions' : t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="font-mono">{filteredLogs.length} records</Badge>
      </div>

      {/* Logs Table */}
      <Card>
        <ScrollArea className="max-h-[600px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Record Type</TableHead>
                <TableHead>Record</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map(log => {
                const IconComp = ACTION_ICONS[log.activity_type] || Activity;
                const colorClass = ACTION_COLORS[log.activity_type] || 'bg-slate-50 text-slate-700 border-slate-200';
                return (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-xs font-mono text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{log.user_name}</p>
                        <p className="text-xs text-muted-foreground">{log.user_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${colorClass} border`}>
                        <IconComp className="h-3 w-3 mr-1" />
                        {log.activity_type?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">{log.record_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{log.record_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[250px] truncate">{log.details || '-'}</TableCell>
                  </TableRow>
                );
              })}
              {filteredLogs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No activity logs found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>
    </div>
  );
};

export default AuditTrailPage;
